import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { FaCheck, FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import "../styles/SignUp.css";
import backendUrl from "../BackendUrl";

const SignUp = () => {
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isValid, setIsValid] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsValid((prev) => ({
      ...prev,
      email: emailRegex.test(e.target.value),
    }));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    const isPasswordValid = passwordRegex.test(e.target.value);
    setIsValid((prev) => ({
      ...prev,
      password: isPasswordValid,
      confirmPassword: e.target.value === confirmPassword && isPasswordValid,
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setIsValid((prev) => ({
      ...prev,
      confirmPassword: e.target.value === password && prev.password,
    }));
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    if (isValid.email && isValid.password && isValid.confirmPassword) {
      setLoading(true);
      setError(null);
      setSuccess(false);
  
      try {
        const response = await axios.post(
          `${backendUrl}/api/accounts/register`,
          { firstName, lastName, email, password, recaptchaToken }
        );
  
        setLoading(false);
        setSuccess(true);
  
        if (response.data.verificationRequired) {
          navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        } else {
          alert(response.data.message || "Registration successful!");
        }
      } catch (err) {
        setLoading(false);
        setError(
          err.response?.data?.message || "An error occurred during registration."
        );
      }
    } else {
      alert("Please ensure all fields meet the requirements.");
    }
  };  

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className="sign-up-container">
      <h1 className="sign-up-heading">Sign Up</h1>
      <p className="sign-up-description">
        Join our platform to unlock exclusive features:
      </p>
      <ul className="sign-up-benefits">
        <li>Save and manage your created cars.</li>
        <li>Securely store your payment methods for faster checkout.</li>
        <li>Access personalized recommendations and special offers.</li>
        <li>Track your orders and view your purchase history.</li>
      </ul>
      <form className="sign-up-form" onSubmit={handleAuth}>
        <label>First Name:</label>
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <label>Last Name:</label>
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
        />
        <label>Email:</label>
        <div>
          <input
            type="text"
            value={email}
            onChange={handleEmailChange}
            required
          />
          {isValid.email ? <FaCheck className="valid-icon" /> : <FaTimes className="invalid-icon" />}
        </div>
        <small>
          {isValid.email ? "" : "Invalid email format (e.g., user@example.com)."}
        </small>
        <label>Password:</label>
        <div className="password-container">
          <input
            type={passwordVisible ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            required
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="password-toggle"
          >
            {passwordVisible ? <FaEyeSlash /> : <FaEye />}
          </button>
          {isValid.password ? <FaCheck className="valid-icon" /> : <FaTimes className="invalid-icon" />}
        </div>
        <small>
          {isValid.password
            ? ""
            : "Password must be at least 8 characters long, include one uppercase letter, and one number."}
        </small>
        <label>Confirm Password:</label>
        <div>
          <input
            type={passwordVisible ? "text" : "password"}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required
          />
          {isValid.confirmPassword ? <FaCheck className="valid-icon" /> : <FaTimes className="invalid-icon" />}
        </div>
        <small>
          {isValid.confirmPassword ? "" : "Passwords must match."}
        </small>
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={handleRecaptcha}
        />
        <small style={{ marginBottom: "1rem", color: !recaptchaToken ? "red" : "green" }}>
          {!recaptchaToken ? "Please verify you are not a robot." : "Verified ✅"}
        </small>
        <button
          type="submit"
          disabled={
            !isValid.email ||
            !isValid.password ||
            !isValid.confirmPassword ||
            !recaptchaToken ||
            loading
          }
        >
          {loading ? "Signing Up..." : "Sign Up"}
        </button>
      </form>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">Registration successful!</p>}
    </div>
  );
};

export default SignUp;